// verificationClient — PUBLIC (no-auth) client for the previous-landlord
// verification form. Like intakeClient, never attaches a Bearer token: the
// /api/verify/:token endpoints are gated solely by the token in the path.
// Mirrors the wire shapes in apps/api/src/routes/rentalVerificationForm.ts.

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

export type VerificationState = 'fillable' | 'submitted' | 'expired' | 'unavailable';

export interface VerificationAddress {
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface VerificationView {
  state: VerificationState;
  tenantNames: string[];
  address: VerificationAddress;
  current: boolean;
  claimed: { residedFrom?: string; residedTo?: string; monthlyRent?: number };
  landlordName?: string;
  /** The applicant's signed authorization is viewable at authorizationUrl(token). */
  hasAuthorization?: boolean;
}

export interface VerificationScreening {
  othersCount?: number;
  properNotice?: boolean;
  properNoticeTerms?: string;
  eviction?: boolean;
  evictionExplain?: string;
  nsfOrLate?: boolean;
  nsfLateDetail?: string;
  outstandingDebt?: boolean;
  debtDetail?: string;
  leaseViolations?: boolean;
  violationsExplain?: string;
  pets?: boolean;
  petsDetail?: string;
  otherConcerns?: boolean;
  otherConcernsDetail?: string;
  rentAgain?: boolean;
  rentAgainWhyNot?: string;
}

export interface VerificationSubmissionBody {
  neverRented?: boolean;
  declined?: boolean;
  declineReason?: string;
  related?: boolean;
  relationshipExplain?: string;
  confirmed?: {
    stillResiding?: boolean;
    moveIn?: string;
    moveOut?: string;
    monthlyRent?: number;
  };
  screening?: VerificationScreening;
  respondent?: { name?: string; email?: string; phone?: string };
}

export async function getVerification(token: string): Promise<VerificationView> {
  const res = await fetch(`${API_BASE}/api/verify/${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error(`Failed to load verification (${res.status})`);
  return res.json() as Promise<VerificationView>;
}

export async function submitVerification(
  token: string,
  body: VerificationSubmissionBody,
): Promise<VerificationView> {
  const res = await fetch(`${API_BASE}/api/verify/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to submit (${res.status})`);
  return res.json() as Promise<VerificationView>;
}

/** The tokenized inline-PDF URL of the applicant's signed authorization. */
export function authorizationUrl(token: string): string {
  return `${API_BASE}/api/verify/${encodeURIComponent(token)}/authorization`;
}
